// New tagged slice for the migrated `_features/usageCompilation` feature -
// consolidates TWO duplicate injectEndpoints calls that both existed on
// the same apiSlice for /usagecompilation routes (the old `features/api/
// usageCompilationApiSlice.js`, since removed entirely, and
// features/usageCompilationFeature/api/usageCompilationApi.js).
//
// Fixes a live, currently-shipping bug in the process: unlike Usage
// Sources' benign fat/lean collision, this one is real. The feature-local
// (lean) file's editUsageCompilation was `PUT /usageCompilation/${_id}` -
// the backend (simplifyserver/routes/usageCompilation.js) has no PUT verb
// at all and no `/:id` route, only `router.patch("/", ...)` reading
// `req.body._id`. Because store.js eagerly imports the feature-local api
// at boot (via the barrel) and RTK Query's injectEndpoints keeps whichever
// definition registers first, the wrong PUT definition was winning over
// the correct PATCH one that already existed, unused, in the integration
// slice - so editing a usage compilation 404'd in production. Fixed here
// by using the integration slice's apiRoutes-driven PATCH definition as
// the only source of truth. Confirmed against the real backend controller,
// not assumed.
//
// Drops deleteUsageCompilation (confirmed zero UI consumers - archive/
// activate uses changeUsageCompilationStatus, not delete - same as every
// prior migration's dead delete endpoint; also confirmed the feature-local
// delete had an equally wrong URL/body shape, so dropping it removes a
// second latent bug rather than porting it forward).
import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

export const usageCompilationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllUsageCompilations: builder.query({
      query: (params) => ({
        url: apiRoutes.usageCompilation.get.path,
        method: apiRoutes.usageCompilation.get.reqType,
        params,
      }),
      providesTags: ["UsageCompilations"],
    }),
    getUsageCompilations: builder.query({
      query: (params) => ({
        url: apiRoutes.usageCompilation.getAll.path,
        method: apiRoutes.usageCompilation.getAll.reqType,
        params,
      }),
      providesTags: ["UsageCompilations"],
    }),
    createUsageCompilation: builder.mutation({
      query: (data) => ({
        url: apiRoutes.usageCompilation.create.path,
        method: apiRoutes.usageCompilation.create.reqType,
        body: data,
      }),
      invalidatesTags: ["UsageCompilations"],
    }),
    editUsageCompilation: builder.mutation({
      query: (data) => ({
        url: apiRoutes.usageCompilation.edit.path,
        method: apiRoutes.usageCompilation.edit.reqType,
        body: data,
      }),
      invalidatesTags: ["UsageCompilations"],
    }),
    changeUsageCompilationStatus: builder.mutation({
      query: (data) => ({
        url: "/usagecompilation/changeStatus",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["UsageCompilations"],
    }),
    checkUsageSource: builder.mutation({
      query: (data) => ({
        url: apiRoutes.usageCompilation.checksource.path,
        method: apiRoutes.usageCompilation.checksource.reqType,
        body: data,
      }),
    }),
  }),
});

export const {
  useGetAllUsageCompilationsQuery,
  useGetUsageCompilationsQuery,
  useCreateUsageCompilationMutation,
  useEditUsageCompilationMutation,
  useChangeUsageCompilationStatusMutation,
  useCheckUsageSourceMutation,
} = usageCompilationApi;
